import {toBaseScalarPricingPayload} from "@/features/pricing";
import {CreateLiveEventFormValues} from "../reducers/create-live-event/types";
import type {CreateLiveEventRequest} from "../../../../features/live-events";
import {toPurchaseLimitPayload} from "../../../../components/offering-form/general/settings/purchase-limit-payload";

/**
 * Converts form values to v2 domain CreateLiveEventRequest format.
 */
export function createLiveEventDto(
  values: CreateLiveEventFormValues
): CreateLiveEventRequest {
  const redirectType =
    values.redirectAfterPurchase === "custom-url"
      ? "url"
      : values.redirectAfterPurchase ?? "none";
  return {
    // The Pricing Model, the base-currency scalars and the rows. Under Pay What You Want
    // the base row is split out of `currencies` on the way; Free and Single Fee send what
    // they always have.
    ...toBaseScalarPricingPayload(values),
    description: values.description,
    hideFromMenu: values.hideFromProfile,
    name: values.name,
    attendees: values.attendees ? values.attendees : -1,
    customLink: values.callLocation === "custom_url" ? values.customUrl : null,
    hasCustomLink: !!values.customUrl && values.callLocation === "custom_url",
    hasGoogleMeet: values.callLocation === "google_meet",
    useZoom: values.callLocation === "zoom",
    hasPhysicalLocation: values.callLocation === "physical_location",
    locationAddress:
      values.callLocation === "physical_location" ? values.locationAddress : null,
    locationLat: values.callLocation === "physical_location" ? values.locationLat : null,
    locationLng: values.callLocation === "physical_location" ? values.locationLng : null,
    isLocationPublic:
      values.callLocation === "physical_location"
        ? values.isLocationPublic !== false
        : true,
    isPaused: values.closeRegistration,
    sessionInfo:
      values.topicType === "series"
        ? values.classSeries.map((item) => ({
            name: item.name,
            startDate: item.startDate,
            startTime: item.startTime,
            duration:
              item.duration === "-1" && item.customDuration
                ? item.customDuration
                : Number(item.duration),
          }))
        : [
            {
              name: values.name,
              startDate: values.startDate as string,
              startTime: values.startTime as string,
              duration:
                values.duration === "-1" && values.customDuration
                  ? values.customDuration
                  : Number(values.duration),
            },
          ],
    topicType: values.topicType,
    requestPhoneNumber: values.requestCustomerPhoneNumber,
    ...toPurchaseLimitPayload(values),
    coverImage: values.coverImage,
    urlRedirect: redirectType === "url" ? values.url_redirect : null,
    redirectType,
    telegramGroupChatId:
      redirectType === "telegram" ? values.telegramGroupId ?? null : null,
    enableCurrencyConversion:
      values.callPriceType === "free"
        ? false
        : values.enable_currency_conversion || false,
    selectedCurrencies:
      values.callPriceType === "free" ? [] : values.selected_currencies || [],
    bookingQuestions:
      values.bookingQuestions.length > 0
        ? values.bookingQuestions.map((q) => {
            if (
              q.answerType === "checkbox" ||
              q.answerType === "dropdown" ||
              q.answerType === "radio-button"
            ) {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answerType as "checkbox" | "dropdown" | "radio-button",
                options: q.options.map((o) => o.optionLabel),
              };
            } else {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answerType as "short-text" | "email" | "radio",
              };
            }
          })
        : [],
  };
}
